import { useEffect, useState } from 'react';
import { ExperienceSlot, useExperiment, type ExperienceRegistry } from '@/lib/experiments';
import { useAppAuth } from '@/lib/useAppAuth';
import { useUsersGetMe } from '@/lib/endpoints';
import { useFtueCompletion } from './ftueCompletion';
import { MakeACallLobby } from './MakeACallLobby';
import { StepByStepLobby } from './StepByStepLobby';
import { FtueBootCover, FtueShell } from './FtueShell';
import { FTUE_EXPERIENCE_FLAG } from './ftueExperiment';

export { FTUE_EXPERIENCE_FLAG };

/**
 * `exp.ftue.experience` — the FTUE experiment (see the experiments layer in
 * `lib/experiments`).
 *
 * The FTUE is a FULL-SCREEN, ONE-SHOT overlay: it plays right after signup
 * (`isOnboarded`), on top of the whole app (mounted with the global overlays
 * in `App.tsx`), opening with a welcome screen. Exiting — the arm's "take me
 * to the real thing", or the shell's Skip — flips the one-shot completion
 * flag, persists the resolution (outcome + variant + timestamp) to the
 * backend, and the overlay never returns. The backend record is
 * authoritative across devices: on a fresh device the gate reads
 * `GET /users/me` before showing anything and re-seeds the local cache.
 *
 * The `control` arm also renders nothing — but through the ExperienceSlot,
 * so its exposure still fires and the experiment keeps its control
 * denominator; it persists `NOT_PARTICIPATING` with the served variant.
 *
 * LaunchDarkly flag (multivariate string, client-side enabled, rollout
 * bucketed by the **device** context kind):
 *   - `control`       → no FTUE (exposure only)
 *   - `make-a-call`   → the hero-question experience, in the FTUE shell
 *   - `step-by-step`  → the 10-second Bitcoin game, in the FTUE shell
 *   - `__excluded`    → not in the experiment: nothing, no exposure
 *
 * Who is *in* the experiment is LD targeting config, not code — serve
 * `__excluded` to everyone the test shouldn't count.
 * QA preview without touching the flag: `/?exp.ftue.experience=<variant>`
 * (clear `predictions:ftue_completed` in localStorage AND the user row's
 * `ftue_completed_at` to replay).
 */
const ControlArm = () => {
  // Control/excluded users will never see the FTUE — settle the one-shot flag
  // quietly (no `ftue_completed` analytics event; nothing was shown or
  // exited) and record NOT_PARTICIPATING + the actual served variant on the
  // backend, so future loads on any device skip the gate entirely.
  const { variant } = useExperiment(FTUE_EXPERIENCE_FLAG);
  const { markCompleted } = useFtueCompletion();
  useEffect(() => {
    markCompleted({ outcome: 'NOT_PARTICIPATING', variant });
  }, [variant, markCompleted]);
  return null;
};
const MakeACallArm = () => (
  <FtueShell variant="make-a-call">
    <MakeACallLobby />
  </FtueShell>
);
const StepByStepArm = () => (
  <FtueShell variant="step-by-step">
    <StepByStepLobby />
  </FtueShell>
);

const FTUE_EXPERIENCES: ExperienceRegistry = {
  control: ControlArm,
  'make-a-call': MakeACallArm,
  'step-by-step': StepByStepArm,
};

/**
 * Synchronous "probably signed in" hint for the auth-init window: the auth
 * client persists its access token here. Guests have none, so they never see
 * the boot cover; a stored session on a not-yet-completed device might be a
 * fresh player about to get the FTUE — cover until auth settles it.
 */
function hasStoredSession(): boolean {
  try {
    return window.localStorage.getItem('predictions:access_token') != null;
  } catch {
    return false;
  }
}

/**
 * The FTUE is for FRESH signups only: past this window an unstarted FTUE is
 * settled EXPIRED instead of shown. An INITIATED one survives the window —
 * a player who bailed mid-FTUE may finish it whenever they return.
 */
const FTUE_FRESHNESS_WINDOW_MS = 12 * 60 * 60 * 1000;

/** Mounted with the global overlays in `App.tsx`. */
export function FtueGate() {
  const { isOnboarded, isLoading } = useAppAuth();
  const { completed, markCompleted, syncFromServer } = useFtueCompletion();
  // The variant the user WOULD receive — recorded on an EXPIRED settle so the
  // experiment analysis can still segment the expired cohort.
  const { variant: assignedVariant } = useExperiment(FTUE_EXPERIENCE_FLAG);
  // Frozen at mount (lazy init keeps the impure Date.now() out of the render
  // body). Hour-scale granularity — staleness within a session is irrelevant.
  const [now] = useState(() => Date.now());

  // The backend record outranks the device cache: before showing the FTUE to
  // an onboarded player with no local flag, ask the profile whether it
  // already resolved elsewhere. Disabled once anything local says done.
  const profile = useUsersGetMe({
    // One attempt, fail-open: holding the boot cover through network retries
    // would blank the app; a missed check just re-runs the FTUE, and the
    // server's first-write-wins keeps the original record intact.
    query: { enabled: isOnboarded && !completed, staleTime: Infinity, retry: false },
  });
  const serverResolved = profile.data?.ftueCompletedAt != null;
  useEffect(() => {
    if (serverResolved) syncFromServer();
  }, [serverResolved, syncFromServer]);

  // Freshness rule: an FTUE that was never initiated goes stale 12h after
  // signup — settle it EXPIRED instead of showing onboarding to someone who
  // is no longer new. Initiated-but-unfinished FTUEs survive the window.
  // A missing signupCompletedAt (accounts predating that column) also counts
  // as stale — those users are the opposite of fresh.
  const p = profile.data;
  const expired =
    p != null &&
    p.ftueCompletedAt == null &&
    p.ftueInitiatedAt == null &&
    (p.signupCompletedAt == null || now - new Date(p.signupCompletedAt).getTime() > FTUE_FRESHNESS_WINDOW_MS);
  useEffect(() => {
    // Quiet settle (no analytics event — nothing was shown), recorded with
    // the variant the user would have received.
    if (expired && !completed) markCompleted({ outcome: 'EXPIRED', variant: assignedVariant });
  }, [expired, completed, assignedVariant, markCompleted]);

  // Already done — the common case after ramp-up: nothing, instantly.
  if (completed || serverResolved || expired) return null;

  // Auth still resolving on a device that holds a session: cover instead of
  // letting the lobby flash, because this may be a fresh FTUE player. The
  // control arm settles its state on first render, so its cohort pays this
  // cover exactly once.
  if (isLoading && hasStoredSession()) return <FtueBootCover />;

  // Not a fresh post-signup player (guest, or pre-onboarding): nothing —
  // and no experiment exposure.
  if (!isOnboarded) return null;

  // The cross-device check is in flight — cover, don't guess: showing the
  // FTUE to a player who finished it on their phone would break "never see
  // it again". A failed profile read falls through to showing the FTUE
  // (first-write-wins on the server makes a duplicate resolution harmless).
  if (profile.isLoading) return <FtueBootCover />;

  // While the assignment resolves, an opaque cover keeps the lobby from
  // flashing behind the FTUE (control users get a sub-second cover, then
  // the lobby).
  return <ExperienceSlot experiment={FTUE_EXPERIENCE_FLAG} registry={FTUE_EXPERIENCES} fallback={<FtueBootCover />} />;
}
